<template>
  <div
    v-if="canBeRendered"
    :id="properties.id"
    class="empty:hidden"
    :class="[hiddenOnDeviceClasses, { 'mb-56': properties.withBottomMargin }]"
    :data-qa="dataQaAttribute"
  >
    <component
      :is="block"
      :id="properties.id"
      :block-position="properties.blockPosition"
      v-bind="properties.props"
      :tracking="trackingBase"
      @error="handleError"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, inject, ref } from 'vue'

import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'

import { useBlockRenderer } from './BlockRenderer.constants'
import {
  BLOCK_RENDERER_PAGE_PARAMS_INJECTION_KEY,
  type BlockRendererProps,
} from './BlockRenderer.types'
import { HIDE_ON_DEVICE_CLASSES, isHiddenOnAllDevices } from './devices'
import { formatDataQaAttribute } from './formatDataQaAttribute'
import { trackingData } from './tracking'

defineOptions({ inheritAttrs: false })

const properties = withDefaults(defineProps<BlockRendererProps>(), {
  withBottomMargin: true,
  hydrationMode: 'auto',
})

const logger = useLogger()

const pageParamsFromRoute = inject(BLOCK_RENDERER_PAGE_PARAMS_INJECTION_KEY, {
  pageType: '',
  pageName: '',
  pageCategory: undefined,
})

const hasEmittedAnError = ref(false)
const { blockExists: blockExistsFn, getBlock } = useBlockRenderer(
  properties.hydrationMode,
)

const isAvailable = computed(() => {
  const blockExists = blockExistsFn(properties.type)
  if (!blockExists) {
    logger.error(
      `The block "${properties.type}" configured in the CMS is not implemented`,
      { owners: ['bot-squad-content-front', 'bot-group-onboard-front'] },
    )
  }

  return blockExists
})

const block = computed(() => getBlock(properties.type))

const dataQaAttribute = computed(() => formatDataQaAttribute(properties.type))

const canBeRendered = computed(
  () =>
    !hasEmittedAnError.value &&
    isAvailable.value &&
    !isHiddenOnAllDevices({ devices: properties.hiddenDevices }),
)

const hiddenOnDeviceClasses = computed(() =>
  properties.hiddenDevices.map((device) => HIDE_ON_DEVICE_CLASSES[device]),
)

const trackingBase = computed(() => {
  return {
    ...(properties.tracking || {}),
    ...trackingData({
      ...pageParamsFromRoute,
      blockName: properties.type,
      blockPosition: properties.blockPosition,
      technicalName: properties.props.technicalName || '',
    }),
  }
})

function handleError() {
  hasEmittedAnError.value = true
}
</script>
